
import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import MessageBubble from "./MessageBubble";
import TypingIndicator from "./TypingIndicator";

interface Message {
  id: string;
  content: string;
  sender_id: string;
  receiver_id: string;
  created_at: string;
  is_read: boolean;
  message_type: string | null;
  file_url: string | null;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  duration: number | null;
  thumbnail_url: string | null;
  location_data: any;
  isPending?: boolean;
  isError?: boolean;
  retryCount?: number;
}

interface MessageReaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
}

interface MatchUser {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  photo_urls: string[] | null;
}

interface ChatMessageListProps {
  messages: Message[];
  reactions: { [messageId: string]: MessageReaction[] };
  onReaction: (messageId: string, emoji: string) => void;
  onRetry: (messageId: string) => void;
  partnerTyping: boolean;
  otherUser: MatchUser | null;
  currentUserId?: string;
  messagesEndRef: React.RefObject<HTMLDivElement>;
}

const ChatMessageList = ({ 
  messages, 
  reactions, 
  onReaction,
  onRetry, 
  partnerTyping, 
  otherUser, 
  currentUserId,
  messagesEndRef
}: ChatMessageListProps) => {
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getPartnerDisplayName = () => {
    if (!otherUser) return "Partner";
    
    if (otherUser.display_name) {
      return otherUser.display_name;
    }
    
    const fullName = `${otherUser.first_name || ""} ${otherUser.last_name || ""}`.trim();
    return fullName || "Partner";
  };

  return (
    <Card className="h-full rounded-none border-x-0 border-t-0 border-b-0">
      <CardContent className="p-0 h-full">
        <ScrollArea className="h-full p-4 pb-36">
          <div className="space-y-2">
            {messages.length === 0 ? (
              <div className="text-center text-gray-500 mt-8">
                <p>Start the conversation!</p>
              </div>
            ) : (
              messages.map((message, index) => {
                const previousMessage = messages[index - 1];
                const showAvatar = !previousMessage || 
                  previousMessage.sender_id !== message.sender_id ||
                  new Date(message.created_at).getTime() - new Date(previousMessage.created_at).getTime() > 300000;
                
                return (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isOwn={message.sender_id === currentUserId}
                    showAvatar={showAvatar}
                    reactions={reactions[message.id] || []}
                    onReaction={onReaction}
                    onRetry={onRetry}
                    partnerName={getPartnerDisplayName()}
                    partnerPhoto={otherUser?.photo_urls?.[0]}
                  />
                );
              })
            )}
            {partnerTyping && (
              <TypingIndicator 
                partnerName={getPartnerDisplayName()}
                partnerPhoto={otherUser?.photo_urls?.[0]}
              />
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default ChatMessageList;
